import apiCall from "../utils/apiInterceptor";
import { ServiceProvider } from "./ServiceProvider";


class DrReportService {

    /**
     * Fetch DR reports with optional pagination and date range.
     *
     * @param {string}      tab       - 'dr_request' | 'dr_approved' | 'dr_completed'
     * @param {number|null} page      - Page number. null = no pagination (full export).
     * @param {number|null} count     - Items per page. null = no pagination.
     * @param {string}      search    - Free-text search string.
     * @param {string}      startDate - "YYYY-MM-DD" or "".
     * @param {string}      endDate   - "YYYY-MM-DD" or "".
     * @returns {{ success, data, count }}
     */
    static async getDrReports(
        tab = 'dr_request',
        page = 1,
        count = 25,
        search = '',
        startDate = '',
        endDate = ''
    ) {
        try {
            const params = new URLSearchParams();

            // Pagination — omit entirely when null (full export mode)
            if (page != null) params.set('page_no', String(page));
            if (count != null) params.set('count', String(count));

            // Status maps 1-to-1 with the tab name
            params.set('status', tab);

            if (search) params.set('search_text', search);
            if (startDate) params.set('start_date', startDate);
            if (endDate) params.set('end_date', endDate);

            const url = `${ServiceProvider.report.getDrRep}${params.toString()}`;
            // ↑ Add ServiceProvider.report.getDrReport = '/api/pdi/report/getDrReport?'
            //   (or adjust to whatever your ServiceProvider uses)

            const response = await apiCall(url, { method: 'GET' });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    data: data.reports || [],
                    count: data.total_count || 0,
                };
            }

            return { success: false, data: [], count: 0 };

        } catch (error) {
            console.error('DrReportService.getDrReports error:', error);
            throw error;
        }
    }

    /**
     * Approve a DR Request record (dr_request → dr_approved).
     *
     * @param {string} reportId - The report _id.
     * @param {string} remark   - Optional approval remark.
     * @returns {{ success, message }}
     */
    static async approveReport(reportId, remark = '') {
        try {
            const url = `${ServiceProvider.report.approveDrReport}`;
            // ↑ e.g. '/api/pdi/report/approveDrReport'

            const response = await apiCall(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ report_id: reportId, remark }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            return { success: !!data.success, message: data.message || '' };

        } catch (error) {
            console.error('DrReportService.approveReport error:', error);
            throw error;
        }
    }

    /**
     * Mark a DR Approved record as complete (dr_approved → dr_completed).
     *
     * @param {string} reportId - The report _id.
     * @param {string} remark   - Optional completion remark.
     * @returns {{ success, message }}
     */
    static async completeReport(reportId, remark = '') {
        try {
            const url = `${ServiceProvider.report.completeDrReport}`;
            // ↑ e.g. '/api/pdi/report/completeDrReport'

            const response = await apiCall(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ report_id: reportId, remark }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            return { success: !!data.success, message: data.message || '' };

        } catch (error) {
            console.error('DrReportService.completeReport error:', error);
            throw error;
        }
    }
}

export default DrReportService;